<template>
  <div class="markdown-content" ref="markdownContainer">
    <!-- New chunked messages -->
    <template v-if="message.chunks?.length">
      <template v-for="(chunk, index) in message.chunks" :key="index">
        <div
          v-if="chunk.type === CHUNK_TYPE.TEXT"
          class="markdown-content"
          ref="textChunks"
          v-html="sanitizeAndParse(chunk.content)"
        />

        <ThinkingCard v-else-if="chunk.type === CHUNK_TYPE.RESEARCH" :title="chunk.data.key">
          <div class="markdown-content" ref="textChunks" v-html="sanitizeAndParse(chunk.data.value)" />
        </ThinkingCard>
      </template>
    </template>

    <!-- Legacy messages -->
    <div v-else class="markdown-content" v-html="renderedMarkdown"></div>
  </div>
</template>

<script>
import { ref, computed, watch, nextTick, onMounted, h, render, inject } from 'vue';
import { marked } from 'marked';

import { CHUNK_TYPE } from '~/constants/conversationModels';

import useDocuments from '~/composables/useDocuments';
import { useSanitize } from '~/composables/useSanitize';

import ThinkingCard from '~/components/conversation/chatMessage/ThinkingCard';
import InfoTooltip from '~/components/conversation/chatMessage/InfoTooltip';

export default {
  name: 'TextMessageWithTooltip',
  props: {
    message: {
      type: Object,
      default: () => ({}),
    },
  },
  components: { ThinkingCard },
  setup(props) {
    const arcaneApiGateway = inject('arcaneApiGateway');
    const { openDocumentFromTitle } = useDocuments();
    const { sanitize } = useSanitize();

    const markdownContainer = ref(null);
    const textChunks = ref([]);

    // Legacy message HTML
    const renderedMarkdown = computed(() => sanitizeAndParse(props.message.message));

    // Function to sanitize and parse text chunks
    const sanitizeAndParse = (content) => {
      const html = content?.replace(
        /\[([^\]]+?)-(\d+),\s*p\.(\d+)\]/g,
        (match, id, snippetIndex, page) =>
          `<span class="citation-placeholder" data-id="${id}" data-index="${snippetIndex}" data-page="${page}"></span>`,
      );
      return props.message.isTyping
        ? sanitize(marked.parse(html !== undefined ? html + '<span v-if="isTyping" class="cursor" />' : ''))
        : sanitize(marked.parse(html !== undefined ? html : ''));
    };

    const handleTooltipTitle = async (payload) => {
      await openDocumentFromTitle(payload);
    };

    const updateTooltipsInElement = (el) => {
      if (!el) return;
      const placeholders = el.querySelectorAll('.citation-placeholder');
      let dict = {};
      let label = 1;

      placeholders.forEach((ph) => {
        const id = ph.getAttribute('data-id');
        const snippetIndex = ph.getAttribute('data-index');
        const page = ph.getAttribute('data-page');

        if (!(`${id}-${snippetIndex}` in dict)) {
          dict[`${id}-${snippetIndex}`] = label;
          label++;
        }

        const vnode = h(InfoTooltip, {
          id,
          page,
          snippetIndex,
          messageData: props.message,
          service: arcaneApiGateway,
          label: dict[`${id}-${snippetIndex}`],
          onTitleClicked: handleTooltipTitle,
        });

        render(vnode, ph);
      });
    };

    const updateAllTooltips = () => {
      // Legacy messages
      if (!props.message.chunks?.length && markdownContainer.value) {
        updateTooltipsInElement(markdownContainer.value);
      }

      // Chunked text messages
      if (props.message.chunks?.length) {
        textChunks.value.forEach((el) => {
          updateTooltipsInElement(el);
        });
      }
    };

    onMounted(() => {
      nextTick(() => {
        updateAllTooltips();
      });
    });

    watch(
      () => [props.message?.data?.length, props.message.chunks],
      () => {
        nextTick(() => {
          updateAllTooltips();
        });
      },
      { deep: true },
    );

    return {
      markdownContainer,
      textChunks,
      renderedMarkdown,
      sanitizeAndParse,
      handleTooltipTitle,
      CHUNK_TYPE,
    };
  },
};
</script>

<style scoped lang="scss">
.markdown-content {
  word-break: break-word;
  font-size: 15px;
  line-height: $bb-spacing-5;
  display: flex;
  flex-direction: column;
  gap: $bb-spacing-4;

  :deep(ul),
  :deep(ol) {
    padding-left: $bb-spacing-6;
    display: flex;
    flex-direction: column;
    gap: $bb-spacing-3;

    //&:not(:last-child) {
    // padding-bottom: $bb-spacing-3;
    //}
  }

  :deep(.cursor) {
    display: inline-block;
    margin-left: $bb-spacing-1;
    width: 2px;
    height: 1em;
    background-color: $bb-primary-3;
    animation: blink-animation 1s steps(20, start) infinite;
    @keyframes blink-animation {
      0%,
      100% {
        opacity: 1;
      }
      50% {
        opacity: 0;
      }
    }
  }
}
</style>
